import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Printer, ArrowRight } from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; } };

function renderAnswer(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v ? "نعم" : "لا";
  if (Array.isArray(v)) return v.filter(Boolean).join("، ");
  if (typeof v === "object") { try { return JSON.stringify(v); } catch { return String(v); } }
  return String(v);
}

export default function AssociationStudentPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get(`/association/student-full/${id}`);
        setData(r);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream-50 p-4">
        <div className="text-rose-700 text-sm">{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream-50">
        <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
      </div>
    );
  }

  const { profile, survey, placement } = data;
  const answers = survey?.answers || {};
  const labels = survey?.labels || {};
  const entries = Object.keys(labels)
    .map((k) => ({ key: k, label: labels[k], val: renderAnswer(answers[k]) }))
    .filter((x) => x.val !== null);

  return (
    <div className="min-h-screen bg-white text-slate-900" dir="rtl" data-testid="student-print-page">
      {/* Toolbar - hidden on print */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowRight className="h-4 w-4" /> رجوع
        </button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600" data-testid="print-btn">
          <Printer className="h-4 w-4" /> طباعة / حفظ PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-5 print:p-6">
        {/* Header */}
        <div className="border-b-2 border-teal-700 pb-4 mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1">منصّة عماد — الملف التعريفي للطالب</div>
          <h1 className="text-2xl font-heading font-black text-slate-900">{profile?.name}</h1>
          {profile?.email && <div className="mt-1 text-sm text-slate-600 ltr-inline">{profile.email}</div>}
        </div>

        {/* Basic info */}
        <Section title="المعلومات الأساسية">
          <TwoCol>
            <Row label="التخصص" value={profile?.major} />
            <Row label="الجامعة" value={profile?.university} />
            <Row label="الكلية" value={profile?.college} />
            <Row label="المدينة" value={profile?.city} />
            <Row label="المعدل" value={profile?.gpa} />
            <Row label="الجوال" value={profile?.phone} />
            <Row label="تاريخ التسجيل" value={fmtDate(profile?.registered_at)} />
          </TwoCol>
        </Section>

        {/* Placement */}
        {placement && (
          <Section title="التسكين الحالي">
            <div className="text-base font-heading font-bold text-slate-900 mb-1">{placement.opportunity_title}</div>
            {placement.opportunity && (
              <div className="text-xs text-slate-600 mb-3 arabic-numerals">
                {[placement.opportunity.city, placement.opportunity.duration_weeks && `${placement.opportunity.duration_weeks} أسبوع`, placement.opportunity.training_start && `يبدأ ${fmtDate(placement.opportunity.training_start)}`].filter(Boolean).join(" · ")}
              </div>
            )}
            <div className="grid grid-cols-4 gap-2 mt-2">
              <Stat label="مهام كلية" value={placement.tasks?.total || 0} />
              <Stat label="مكتملة" value={placement.tasks?.done || 0} />
              <Stat label="ساعات محتسبة" value={placement.tasks?.expected_hours || 0} />
              <Stat label="تقارير أسبوعية" value={placement.reports_count || 0} />
            </div>
          </Section>
        )}

        {/* Full survey */}
        <Section title="إجابات الاستبيان الأولي كاملة">
          {!survey?.completed ? (
            <div className="text-sm text-amber-700">لم يُكمل الطالب استبيانه الأولي بعد.</div>
          ) : entries.length === 0 ? (
            <div className="text-sm text-slate-500">لا توجد إجابات مسجّلة.</div>
          ) : (
            <div className="grid grid-cols-1 gap-1.5">
              {entries.map((e) => (
                <div key={e.key} className="grid grid-cols-3 gap-3 py-1.5 border-b border-slate-100">
                  <div className="col-span-1 text-xs font-bold text-teal-700">{e.label}</div>
                  <div className="col-span-2 text-sm text-slate-800 whitespace-pre-line break-words">{e.val}</div>
                </div>
              ))}
            </div>
          )}
          {survey?.submitted_at && (
            <div className="mt-3 text-[11px] text-slate-400 arabic-numerals">
              تاريخ إرسال الاستبيان: {fmtDate(survey.submitted_at)}
            </div>
          )}
        </Section>

        <div className="mt-8 text-[10px] text-slate-400 text-center border-t border-slate-100 pt-3">
          طُبع من منصّة عماد — {new Date().toLocaleString("ar-SA")}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 break-inside-avoid">
      <h2 className="text-sm font-heading font-black text-teal-700 uppercase tracking-widest border-b border-teal-100 pb-1 mb-3">{title}</h2>
      {children}
    </section>
  );
}
function TwoCol({ children }) { return <div className="grid grid-cols-2 gap-x-6 gap-y-1">{children}</div>; }
function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-2 border-b border-slate-100 py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800 text-end text-sm">{value || "—"}</span>
    </div>
  );
}
function Stat({ label, value }) {
  return (
    <div className="p-2 rounded-lg bg-teal-50 border border-teal-100 text-center">
      <div className="text-xl font-heading font-black text-teal-800 arabic-numerals">{value}</div>
      <div className="text-[10px] text-slate-600">{label}</div>
    </div>
  );
}
